import React from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { Location } from "@types";
import ColorPicker from "@components/common/ColorPicker";
import SimpleHiveIcon from "@components/hives/SimpleHiveIcon";

export interface HiveFormValues {
  name: string;
  location_id: number | null;
  hive_type: number | null;
  color: string;
  bee_race: string;
  order: number;
  label: string;
  notes: string;
}

interface HiveType {
  id: number;
  name: string;
  type: string;
  description: string;
}

interface HiveFormFieldsProps {
  control: Control<HiveFormValues>;
  errors: FieldErrors<HiveFormValues>;
  apiaries: Location[];
  hiveTypes: HiveType[];
  beeRaces: { value: string; label: string }[];
  loadingHiveTypes: boolean;
  loadingBeeRaces: boolean;
  hiveColor: string;
  onColorChange: (color: string) => void;
  locationDisabled?: boolean;
  showOrder?: boolean;
}

const sectionBoxSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  p: { xs: 2, md: 3 },
  backgroundColor: "background.paper",
};

const HiveFormFields: React.FC<HiveFormFieldsProps> = ({
  control,
  errors,
  apiaries,
  hiveTypes,
  beeRaces,
  loadingHiveTypes,
  loadingBeeRaces,
  hiveColor,
  onColorChange,
  locationDisabled = false,
  showOrder = false,
}) => {
  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="overline"
          display="block"
          gutterBottom
          color="text.secondary"
        >
          Basic information
        </Typography>
        <Box sx={sectionBoxSx}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Hive name is required",
                  maxLength: {
                    value: 30,
                    message: "Name must be 30 characters or less",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Hive Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    inputProps={{ maxLength: 30 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!errors.location_id}
                disabled={locationDisabled}
              >
                <InputLabel>Apiary</InputLabel>
                <Controller
                  name="location_id"
                  control={control}
                  rules={{ required: "Apiary is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      label="Apiary"
                    >
                      {apiaries.map((apiary) => (
                        <MenuItem key={apiary.id} value={apiary.id}>
                          {apiary.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.location_id && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 2 }}
                  >
                    {errors.location_id.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="label"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Label (optional)"
                    placeholder="e.g., Production hive"
                  />
                )}
              />
            </Grid>

            {showOrder && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="order"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Display order"
                      type="number"
                      helperText="Controls the order of this hive in the apiary list"
                      inputProps={{ min: 0, step: 1 }}
                      value={field.value || 0}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      <Box>
        <Typography
          variant="overline"
          display="block"
          gutterBottom
          color="text.secondary"
        >
          Hive configuration
        </Typography>
        <Box sx={sectionBoxSx}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose how this hive appears and what kind of colony it holds.
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <SimpleHiveIcon size="xl" color={hiveColor} />
                <Box
                  sx={{
                    mt: 2,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Hive color
                  </Typography>
                  <ColorPicker color={hiveColor} onChange={onColorChange} />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    disabled={loadingHiveTypes}
                  >
                    <InputLabel>Hive Type</InputLabel>
                    <Controller
                      name="hive_type"
                      control={control}
                      rules={{ required: "Hive type is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Hive Type"
                          value={field.value || ""}
                        >
                          {loadingHiveTypes ? (
                            <MenuItem value="">
                              <CircularProgress size={20} /> Loading...
                            </MenuItem>
                          ) : (
                            hiveTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                {type.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth disabled={loadingBeeRaces}>
                    <InputLabel>Bee Race</InputLabel>
                    <Controller
                      name="bee_race"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Bee Race"
                          value={field.value || ""}
                        >
                          <MenuItem value="">
                            <em>Not specified</em>
                          </MenuItem>
                          {loadingBeeRaces ? (
                            <MenuItem value="">
                              <CircularProgress size={20} /> Loading...
                            </MenuItem>
                          ) : (
                            beeRaces.map((race) => (
                              <MenuItem key={race.value} value={race.value}>
                                {race.label}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      )}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Notes (optional)"
                        multiline
                        rows={4}
                        placeholder="Add any notes about this hive..."
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default HiveFormFields;
